import React from "react";
import {
  AnalyticsSection,
  StatCard,
  AreaChart,
  PieChart,
} from "./ChartComponents";
import { months, emailData } from "./analyticsData";

const EmailsAnalytics = () => {
  return (
    <div>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard title="Total Emails Sent" value={emailData.totalEmails} />
        <StatCard title="Average Open Rate" value={emailData.openRate} />
        <StatCard
          title="Click-Through Rate"
          value={emailData.clickThroughRate}
        />
        <StatCard
          title="Average Time Taken to Open"
          value={emailData.averageResponseTime}
        />
      </div>

      {/* Email Engagement */}
      <AnalyticsSection title="Email engagement rates">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">
            Monthly email open and click-through rates
          </p>

          <div className="mt-4">
            <AreaChart
              series={[
                emailData.emailEngagement.openRateSeries,
                emailData.emailEngagement.clickRateSeries,
              ]}
              labels={months}
              height={300}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Average Open Rate</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  emailData.emailEngagement.openRateSeries.data.reduce(
                    (a, b) => a + b,
                    0
                  ) / 12
                )}
                %
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Average Click Rate</p>
              <p className="text-xl font-bold text-cpaGold">
                {(
                  emailData.emailEngagement.clickRateSeries.data.reduce(
                    (a, b) => a + b,
                    0
                  ) / 12
                ).toFixed(1)}
                %
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">
                Best Performing Month
              </p>
              <p className="text-xl font-bold text-cpaGold">
                {
                  months[
                    emailData.emailEngagement.openRateSeries.data.indexOf(
                      Math.max(...emailData.emailEngagement.openRateSeries.data)
                    )
                  ]
                }
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Open Rate Growth</p>
              <p className="text-xl font-bold text-cpaGold">
                {emailData.emailEngagement.openRateSeries.data[11] -
                  emailData.emailEngagement.openRateSeries.data[0]}
                %
              </p>
            </div>
          </div>
        </div>
      </AnalyticsSection>

      {/* Email Types and Recent Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <AnalyticsSection title="Email campaigns by type">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <PieChart
              segments={emailData.emailsByType}
              height={450}
              segmentValueLabel="emails"
            />
          </div>
        </AnalyticsSection>

        <AnalyticsSection title="Recent email campaigns">
          <div className="space-y-4">
            {[
              {
                name: "May Newsletter",
                type: "Newsletter",
                sent: "May 31, 2023",
                opens: "34%",
                clicks: "9.2%",
              },
              {
                name: "Summer Mixer Invitation",
                type: "Events",
                sent: "May 24, 2023",
                opens: "41%",
                clicks: "12.5%",
              },
              {
                name: "Member Spotlight: XYZ Company",
                type: "Newsletter",
                sent: "May 17, 2023",
                opens: "38%",
                clicks: "10.1%",
              },
              {
                name: "Business Workshop Reminder",
                type: "Events",
                sent: "May 10, 2023",
                opens: "29%",
                clicks: "8.7%",
              },
              {
                name: "New Member Announcement",
                type: "Membership",
                sent: "May 3, 2023",
                opens: "36%",
                clicks: "7.4%",
              },
            ].map((campaign, index) => (
              <div
                key={index}
                className="flex flex-col p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {campaign.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {campaign.type} • Sent: {campaign.sent}
                    </p>
                  </div>
                  <div className="flex space-x-4 text-sm">
                    <div className="text-right">
                      <p className="text-gray-500">Opens</p>
                      <p className="font-medium text-cpaGold">
                        {campaign.opens}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500">Clicks</p>
                      <p className="font-medium text-cpaGold">
                        {campaign.clicks}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnalyticsSection>
      </div>

      {/* Subscriber Analytics */}
      <AnalyticsSection title="Subscriber growth and engagement">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Top 100 Opening Subscribers
            </h3>
            <div className="space-y-3">
              {[
                { name: "John Smith - ABC Company", rate: "94%" },
                { name: "Jane Doe - XYZ Industries", rate: "91%" },
                { name: "Robert Johnson - 123 Corp", rate: "87%" },
                { name: "Sarah Williams - Local Business", rate: "85%" },
                { name: "Michael Brown - City Bank", rate: "82%" },
              ].map((sub, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">{sub.name}</span>
                  <span className="text-sm font-medium text-cpaGold">
                    {sub.rate}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Best Performing Times
            </h3>
            <div className="space-y-3">
              {[
                { time: "Tuesday, 10am-12pm", rate: "41%" },
                { time: "Thursday, 1pm-3pm", rate: "38%" },
                { time: "Wednesday, 9am-11am", rate: "36%" },
                { time: "Monday, 2pm-4pm", rate: "32%" },
                { time: "Friday, 11am-1pm", rate: "29%" },
              ].map((time, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">{time.time}</span>
                  <span className="text-sm font-medium text-cpaGold">
                    {time.rate}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Newsletter/Email Subscriber Growth
            </h3>
            <div className="text-center py-4">
              <div className="text-3xl font-bold text-cpaGold">854</div>
              <p className="text-sm text-gray-500 mt-1">Total Subscribers</p>

              <div className="mt-4 flex items-center justify-center space-x-3">
                <span className="text-green-600 font-medium">+12%</span>
                <span className="text-sm text-gray-500">vs. last quarter</span>
              </div>

              <div className="mt-4">
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-cpaGold rounded-full"
                    style={{ width: "85%" }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>0</span>
                  <span>Goal: 1,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnalyticsSection>
    </div>
  );
};

export default EmailsAnalytics;
